import { useEffect, useRef } from 'react';
import {
  Box,
  Stack,
  Text,
  EmptyState,
  Skeleton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  DeliveryIcon,
} from '@citibox-panel/ui';
import { track } from '@citibox-panel/analytics';
import type { UserSearchHit, UserDeliveriesResponse, Delivery } from '@citibox-panel/api-client';
import { countBucket } from '../../../lib/countBucket';
import { DELIVERY_COLUMN_HEADERS } from '../../../lib/deliveryColumns';
import { useAccount } from '../../search-shell/hooks/useAccount';
import { useNoticeOnError } from '../../search-shell/hooks/useErrorNotice';
import { useSearchTabs } from '../../search-shell/hooks/useSearchTabs';
import type { SearchTab } from '../../search-shell/model/shellState';
import { useUserDeliveries } from '../hooks/useUserDeliveries';
import { DeliveryRow } from './DeliveryRow';

// The failure's copy and the source it is keyed by (ADR 0021). Named by the TYPE of list — never
// by the titular — so the notice can float over whatever the agent opens next without carrying
// «Usuario» PII with it. The price, accepted with the decision: two «Usuario» tabs failing at once
// read identically.
// TODO: i18n
const READ_ERROR = 'No se pudieron cargar las entregas del «Usuario».';
const NOTICE_ID = 'user-deliveries';

export interface DeliveriesLayoutProps {
  search: SearchTab;
  searchUsers: (phone: string) => Promise<UserSearchHit[]>;
  fetchDeliveries: (id: string) => Promise<UserDeliveriesResponse>;
}

function DeliveriesHeader({ count }: { count: number | undefined }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <DeliveryIcon fontSize="small" />
      {/* Bold section title + count, consistent with the bold field labels. */}
      <Text variant="subtitle2" bold>
        {count === undefined ? 'Entregas' : `Entregas · ${String(count)}`}
      </Text>
    </Box>
  );
}

// The deliveries section owns its query so it loads and fails in isolation from the
// «Usuario» ficha sharing the card: a failure is reported through the shell's floating notice and
// never reaches the account fields, which stay readable. It re-resolves the numeric USER ID from
// the same phone-search cache the ficha uses (a warm hit, no extra request), then reads the
// deliveries by that id.
export function DeliveriesLayout({ search, searchUsers, fetchDeliveries }: DeliveriesLayoutProps) {
  const { account } = useAccount(search, searchUsers);
  const id = account?.id;
  const { data, isPending, isError } = useUserDeliveries(id, fetchDeliveries);
  useNoticeOnError(isError, NOTICE_ID, READ_ERROR);
  const { openDelivery, searches } = useSearchTabs();

  const openIds = new Set(
    searches.find((s) => s.key === search.key)?.deliveries.map((d) => d.id) ?? [],
  );

  // Emit the view event once per «Usuario», after the deliveries settle with data, with
  // categorical props and no PII. A retry does not re-emit (same id); an error does not
  // emit (no count to report).
  const emittedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (id === undefined || isPending || data === undefined) return;
    if (emittedFor.current === id) return;
    emittedFor.current = id;
    track('user_deliveries_viewed', {
      count_bucket: countBucket(data.count),
      has_shared: String(data.deliveries.some((d) => d.sharedWithRecipient)),
    });
  }, [id, isPending, data]);

  const handleOpen = (delivery: Delivery): void => {
    // The sub-tab label is the delivery's tracking, set at open time; a null tracking
    // falls back to the shell's "Entrega {short id}". openDelivery does not relabel an
    // already-open tab.
    openDelivery(search.key, delivery.id, delivery.tracking ?? undefined);
    track('delivery_opened_from_list', {
      shared: String(delivery.sharedWithRecipient),
      status: delivery.status,
      source: 'user',
    });
  };

  // Reported after a successful copy, with the same categorical props as the open event —
  // no PII: the copied phone and tracking never reach analytics.
  const handleCopied = (delivery: Delivery): void => {
    track('delivery_copied_from_list', {
      shared: String(delivery.sharedWithRecipient),
      status: delivery.status,
      source: 'user',
    });
  };

  return (
    // Collapse the overline header labels' tall 2.66 line-height so the column-header
    // row isn't oversized, and bold them (overline appears only in the table head here).
    <Stack
      spacing={2}
      sx={{ '& .MuiTypography-overline': { lineHeight: 1.2, fontWeight: 'bold' } }}
    >
      <DeliveriesHeader count={data?.count} />
      {isPending ? (
        <Stack spacing={1}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={32} />
          ))}
        </Stack>
      ) : isError ? (
        // Nothing in place: the notice above said it, and the pattern gives it no action (ADR 0021).
        // What is left is the section header alone — a remount is what reloads the list.
        null
      ) : data !== undefined && data.count === 0 ? (
        // TODO: i18n
        <EmptyState title="Sin entregas en los últimos 12 meses" />
      ) : (
        <Table aria-label="Entregas">
          <TableHead>
            <TableRow>
              <TableCell component="th" scope="col" />
              {DELIVERY_COLUMN_HEADERS.map((header) => (
                <TableCell key={header} component="th" scope="col">
                  <Text variant="overline" color="secondary">
                    {header}
                  </Text>
                </TableCell>
              ))}
              {/* Trailing header cell for the copy-button column, empty like the leading one. */}
              <TableCell component="th" scope="col" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.deliveries ?? []).map((delivery) => (
              <DeliveryRow
                key={delivery.id}
                delivery={delivery}
                selected={openIds.has(delivery.id)}
                onOpen={() => {
                  handleOpen(delivery);
                }}
                phone={account?.phone ?? ''}
                onCopied={() => {
                  handleCopied(delivery);
                }}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}
